// DEPLOY: /var/www/woody.wtf/frontend/src/chart/asxLive.js
// Today's ASX candle straight from asx.com.au's chart service, fetched by the viewer's browser
// (no proxy cost to us). The request runs inside one hidden sandboxed iframe: a sandboxed frame has
// no origin, so the ASX sees "Origin: null" and no referrer - woody.wtf is never sent. The ASX data
// is 20 minutes delayed. Nothing fetched here is sent back to our server.
let frame = null, ready = null, seq = 0;
const waiting = new Map();

const SRC = `<!doctype html><meta charset="utf-8"><script>
addEventListener('message', async (e) => {
  const m = e.data || {};
  if (!m.id) return;
  try {
    const r = await fetch(m.url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(m.body),
                                   referrerPolicy: 'no-referrer', credentials: 'omit', cache: 'no-store' });
    parent.postMessage({ id: m.id, ok: r.ok, data: r.ok ? await r.json() : null, error: r.ok ? null : 'HTTP ' + r.status }, '*');
  } catch (err) { parent.postMessage({ id: m.id, ok: false, error: String(err) }, '*'); }
});
parent.postMessage({ ready: true }, '*');
<\/script>`;

function ensureFrame() {
  if (ready) return ready;
  ready = new Promise((resolve) => {
    frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-scripts');          // no allow-same-origin -> opaque "null" origin
    frame.setAttribute('referrerpolicy', 'no-referrer');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.display = 'none';
    window.addEventListener('message', (ev) => {
      if (!frame || ev.source !== frame.contentWindow) return;
      const m = ev.data || {};
      if (m.ready) { resolve(); return; }
      const w = waiting.get(m.id);
      if (!w) return;
      waiting.delete(m.id);
      if (m.ok) w.resolve(m.data); else w.reject(new Error(m.error || 'ASX request failed'));
    });
    frame.srcdoc = SRC;
    document.body.appendChild(frame);
  });
  return ready;
}

function request(url, body) {
  return ensureFrame().then(() => new Promise((resolve, reject) => {
    const id = ++seq;
    waiting.set(id, { resolve, reject });
    frame.contentWindow.postMessage({ id, url, body }, '*');
    setTimeout(() => { if (waiting.delete(id)) reject(new Error('ASX request timed out')); }, 20000);
  }));
}

// Rows [isoDate, o, h, l, c, v] from the chart service's response (series aligned at the newest end).
function rows(j) {
  const dates = j?.Dates || [], ser = {};
  for (const el of j?.Elements || []) for (const c of el.ComponentSeries || []) ser[c.Type] = c.Values || [];
  const keys = ['Open', 'High', 'Low', 'Close', 'Volume'];
  const n = Math.min(dates.length, ...keys.map(k => (ser[k] || []).length));
  const out = [];
  for (let i = 0; i < n; i++) out.push([dates[dates.length - n + i], ...keys.map(k => ser[k][ser[k].length - n + i])]);
  return out;
}

const sydneyDate = (d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(d);

function body(xid, period, days) {
  const el = (label, type) => ({ Label: label, Type: type, Symbol: xid, OverlayIndicators: [], Params: {} });
  return { days, dataNormalized: false, dataPeriod: period, dataInterval: 1, realtime: false, yFormat: '0.###',
           timeServiceFormat: 'JSON', returnDateType: 'ISO8601', endOffsetDays: 0,
           elements: [el('price', 'price'), el('volume', 'volume')] };
}

// asx = {xid, url} from /api/chart/live. Resolves {d, o, h, l, c, v, quoteTime} for today (Sydney), or null.
export async function asxToday(asx) {
  const today = sydneyDate();
  const day = rows(await request(asx.url, body(asx.xid, 'Day', 3))).filter(r => String(r[0]).slice(0, 10) === today).pop();
  if (day && day.slice(1, 5).every(v => v != null)) {
    return { d: today, o: day[1], h: day[2], l: day[3], c: day[4], v: day[5] || 0, quoteTime: null };
  }
  const mins = rows(await request(asx.url, body(asx.xid, 'Minute', 1)))
    .filter(r => r.slice(1, 5).every(v => v != null) && sydneyDate(new Date(String(r[0]).replace(/Z?$/, 'Z'))) === today);
  if (!mins.length) return null;
  return { d: today, o: mins[0][1], h: Math.max(...mins.map(r => r[2])), l: Math.min(...mins.map(r => r[3])),
           c: mins[mins.length - 1][4], v: mins.reduce((a, r) => a + (r[5] || 0), 0),
           quoteTime: String(mins[mins.length - 1][0]).replace(/Z?$/, 'Z') };
}
